// eslint-disable-next-line max-classes-per-file
import {
  GetOperation,
  GetListOperation,
  PostOperation,
} from '@/api/rest/base/operations';
import BaseApi from './base/BaseApi';

/**
 * Чтение спецификации заказчика: в отказе нужен код ответа, а не только тело. По нему страница
 * выбирает сообщение «неверная ссылка» (400) или «слишком много запросов» (429) — ТЗ 3.3, 6.6.
 * Остальные операции разбирают отказ как весь остальной модуль. Сгенерировано Claude.
 */
class CustomerSpecGetOperation extends GetOperation {
  // eslint-disable-next-line class-methods-use-this
  modifyError(error) {
    return { status: error?.response?.status ?? null, data: error?.response?.data ?? null };
  }
}

/**
 * То же для списка: чаты заказчика открываются по той же ссылке и отказывают по тем же причинам,
 * что и таблица, — неверная подпись, черновик, исчерпанный ограничитель частоты. Поэтому и
 * сообщение выбирается по коду ответа, а не одно на все случаи (ТЗ 3.3, 6.9).
 */
class CustomerSpecGetListOperation extends GetListOperation {
  // eslint-disable-next-line class-methods-use-this
  modifyError(error) {
    return { status: error?.response?.status ?? null, data: error?.response?.data ?? null };
  }
}

/**
 * Публичные операции заказчика по персональной ссылке (ТЗ 4.3): все принимают внешний идентификатор
 * спецификации и подпись — у чтения подпись идёт параметром `token`, у записи полем тела или формы.
 * Сессии у заказчика нет, поэтому адреса не строятся от `entityName`: это отдельная группа
 * маршрутов `/api/spec/ext/{идентификатор}/…` (ТЗ 3.3, 6.1). Сгенерировано Claude.
 */
export default class SpecCustomerApi extends BaseApi {
  static entityName = 'spec/ext';

  /** Корень публичных операций одной спецификации. */
  // eslint-disable-next-line class-methods-use-this
  root(extId) {
    return `/api/spec/ext/${encodeURIComponent(extId)}`;
  }

  /** Спецификация в срезе заказчика: шапка, свод и позиции (ТЗ 4.3, 3.5, 6.6). */
  fetchSpecification(extId, token, withPreloader = false) {
    const operation = new CustomerSpecGetOperation(this.root(extId), { token });
    return this.execute(operation, withPreloader);
  }

  /** Утвердить позиции одной операцией; ответ — обновлённая спецификация (ТЗ 4.3). */
  approve(extId, token, items, withPreloader = false) {
    const operation = new PostOperation(`${this.root(extId)}/approve`, { token, items });
    return this.execute(operation, withPreloader);
  }

  /** Отклонить позицию с причиной и вложениями; ответ — обновлённая спецификация (ТЗ 4.3). */
  reject(extId, token, { item, reason, files = [] }, withPreloader = false) {
    const operation = new PostOperation(`${this.root(extId)}/reject`, {
      token, item, reason, files,
    });
    return this.execute(operation, withPreloader);
  }

  /**
   * Публичная загрузка файла по ссылке (ТЗ 3.3, 4.3): многочастный запрос с полями `token`
   * и `file`. Ответ — данные файла с идентификатором для отклонения или сообщения.
   */
  uploadFile(extId, token, file, withPreloader = false) {
    const form = new FormData();
    form.append('token', token);
    form.append('file', file, file.name);
    const operation = new PostOperation(`${this.root(extId)}/files`, form);
    return this.execute(operation, withPreloader);
  }

  /** Чаты с участием заказчика (ТЗ 4.3 «Мои чаты», 6.10). */
  fetchChats(extId, token, withPreloader = false) {
    const operation = new CustomerSpecGetListOperation(`${this.root(extId)}/chats`, { token });
    return this.execute(operation, withPreloader);
  }

  /** Лента одного чата: сообщения и системные события (ТЗ 4.3, 6.10). */
  fetchMessages(extId, token, chatId, withPreloader = false) {
    const operation = new GetListOperation(`${this.root(extId)}/chats/${chatId}/messages`, { token });
    return this.execute(operation, withPreloader);
  }

  /** Сообщение заказчика в свой чат (ТЗ 4.3). */
  sendMessage(extId, token, chatId, { text, files = [] }, withPreloader = false) {
    const operation = new PostOperation(`${this.root(extId)}/chats/${chatId}/messages`, {
      token, text, files,
    });
    return this.execute(operation, withPreloader);
  }
}
